import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolCompressionMode,
  type ToolErrorCode,
  type ToolOperation,
} from '@saas-pages/shared/public/runtime'
import {
  parseTargetBytes,
  searchTargetSize,
  type TargetSizeStopReason,
} from '@/pages/compress-image/model/targetSizeCompression'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type CompressStatus = 'pending' | 'compressing' | 'done' | 'unreachable' | 'error'
type CompressMode = ToolCompressionMode
type TargetSizeUnit = 'KB' | 'MB'
type TargetSizePreset = '50' | '100' | '200' | '500' | 'custom'
type SupportedMime = 'image/jpeg' | 'image/png' | 'image/webp'
type SupportedFormat = 'JPG' | 'PNG' | 'WebP'
type CompressErrorCode =
  | 'unsupported_type'
  | 'too_large'
  | 'too_large_image'
  | 'unsupported_webp'
  | 'corrupt_image'
  | 'canvas_error'
  | 'compression_failed'
  | 'target_unreachable'
  | 'search_limit'
  | 'time_limit'

type CompressCopy = Translations['compressImage']

interface CompressEntry {
  id: string
  file: File
  originalUrl: string | null
  originalSize: number
  compressedBlob: Blob | null
  compressedUrl: string | null
  compressedSize: number | null
  resultWidth: number | null
  resultHeight: number | null
  actualQuality: number | null
  targetReached: boolean | null
  status: CompressStatus
  error?: string
  note?: string
}

interface CompressionRequest {
  mode: CompressMode
  quality: number
  targetBytes: number | null
  signal: AbortSignal
}

interface CompressionResult {
  blob: Blob
  width: number
  height: number
  actualQuality: number | null
  targetReached: boolean | null
  stopReason?: TargetSizeStopReason
  note?: string
}

interface ActiveCompressionRun {
  controller: AbortController
  operation: ToolOperation
  settled: boolean
}

const UTILITY_KEY = 'compress-image'
const MAX_FILE_SIZE = 50 * 1024 * 1024
const MAX_PIXELS = 36_000_000
const MAX_DIMENSION = 32_768
const ACCEPTED_TYPES: SupportedMime[] = ['image/jpeg', 'image/png', 'image/webp']
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp']

let nextId = 0
let webpEncodeSupport: Promise<boolean> | null = null

function analyticsContext(mode: CompressMode): ToolAnalyticsContext {
  return {
    app: 'imgify',
    domain: 'imgify.ru',
    route: '/szhat-izobrazhenie',
    utility: UTILITY_KEY,
    processingMode: 'local',
    compressionMode: mode,
  }
}

function analyticsErrorCode(code: CompressErrorCode): ToolErrorCode {
  switch (code) {
    case 'unsupported_type':
    case 'corrupt_image':
      return 'invalid_input'
    case 'unsupported_webp':
      return 'unsupported_output'
    case 'too_large':
    case 'too_large_image':
      return 'size_limit'
    case 'target_unreachable':
      return 'target_unreachable'
    case 'time_limit':
      return 'timeout'
    case 'search_limit':
    case 'canvas_error':
    case 'compression_failed':
    default:
      return 'processing_failed'
  }
}

function settleRunSuccess(run: ActiveCompressionRun, durationMs: number): void {
  if (run.settled) return
  run.settled = true
  run.operation.success(durationMs)
}

function settleRunError(
  run: ActiveCompressionRun,
  errorCode: ToolErrorCode,
): void {
  if (run.settled) return
  run.settled = true
  run.operation.error(errorCode)
}

function cancelRun(run: ActiveCompressionRun): void {
  run.controller.abort()
  settleRunError(run, 'cancelled')
}

function uid(): string {
  return `compress-image-${++nextId}-${Date.now()}`
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function isSupportedImage(file: File): boolean {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return (
    ACCEPTED_TYPES.includes(type as SupportedMime) ||
    ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))
  )
}

function sourceMime(file: File): SupportedMime {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()

  if (type === 'image/png' || name.endsWith('.png')) return 'image/png'
  if (type === 'image/webp' || name.endsWith('.webp')) return 'image/webp'
  return 'image/jpeg'
}

function sourceFormat(file: File): SupportedFormat {
  const mime = sourceMime(file)
  if (mime === 'image/png') return 'PNG'
  if (mime === 'image/webp') return 'WebP'
  return 'JPG'
}

function displayFormat(entry: CompressEntry): SupportedFormat | 'FILE' {
  return isSupportedImage(entry.file) ? sourceFormat(entry.file) : 'FILE'
}

function validateFile(
  file: File,
  copy: CompressCopy,
): { message: string; code: CompressErrorCode } | null {
  if (!isSupportedImage(file)) {
    return { message: copy.errorsText.unsupported, code: 'unsupported_type' }
  }
  if (file.size > MAX_FILE_SIZE) {
    return { message: copy.errorsText.tooLarge, code: 'too_large' }
  }
  return null
}

function outputExtension(mime: SupportedMime): string {
  if (mime === 'image/jpeg') return 'jpg'
  if (mime === 'image/webp') return 'webp'
  return 'png'
}

function outputFilename(inputName: string, mime: SupportedMime): string {
  const base = inputName.replace(/\.[^.]+$/, '') || 'image'
  return `${base}-compressed.${outputExtension(mime)}`
}

function uniqueOutputFilename(
  inputName: string,
  mime: SupportedMime,
  usedNames: Set<string>,
): string {
  const filename = outputFilename(inputName, mime)
  if (!usedNames.has(filename)) {
    usedNames.add(filename)
    return filename
  }

  const extension = outputExtension(mime)
  const base = filename.replace(new RegExp(`\\.${extension}$`, 'i'), '')
  let counter = 2
  let next = `${base}-${counter}.${extension}`
  while (usedNames.has(next)) {
    counter += 1
    next = `${base}-${counter}.${extension}`
  }
  usedNames.add(next)
  return next
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

class CompressImageError extends Error {
  constructor(
    message: string,
    readonly code: CompressErrorCode,
  ) {
    super(message)
  }
}

function abortError(): Error {
  const error = new Error('Compression aborted')
  error.name = 'AbortError'
  return error
}

function assertActive(signal: AbortSignal): void {
  if (signal.aborted) {
    throw abortError()
  }
}

function loadImage(
  src: string,
  copy: CompressCopy,
  signal: AbortSignal,
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    const cleanup = () => {
      signal.removeEventListener('abort', handleAbort)
      image.onload = null
      image.onerror = null
    }
    const handleAbort = () => {
      cleanup()
      image.src = ''
      reject(abortError())
    }
    image.onload = () => {
      cleanup()
      if (signal.aborted) {
        reject(abortError())
        return
      }
      resolve(image)
    }
    image.onerror = () => {
      cleanup()
      reject(new CompressImageError(copy.errorsText.corrupt, 'corrupt_image'))
    }
    signal.addEventListener('abort', handleAbort, { once: true })
    if (signal.aborted) {
      handleAbort()
      return
    }
    image.src = src
  })
}

function supportsWebpEncode(): Promise<boolean> {
  if (!webpEncodeSupport) {
    webpEncodeSupport = new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      canvas.width = 1
      canvas.height = 1
      if (!canvas.toBlob) {
        resolve(false)
        return
      }
      canvas.toBlob((blob) => resolve(blob?.type === 'image/webp'), 'image/webp', 0.8)
    })
  }
  return webpEncodeSupport
}

function encodeCanvas(
  canvas: HTMLCanvasElement,
  mime: SupportedMime,
  quality: number,
  copy: CompressCopy,
  signal: AbortSignal,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    assertActive(signal)
    canvas.toBlob(
      (blob) => {
        if (signal.aborted) {
          reject(abortError())
          return
        }
        if (!blob) {
          reject(new CompressImageError(copy.errorsText.compression, 'compression_failed'))
          return
        }
        if (mime === 'image/webp' && blob.type !== 'image/webp') {
          reject(new CompressImageError(copy.errorsText.unsupportedWebp, 'unsupported_webp'))
          return
        }
        resolve(blob)
      },
      mime,
      mime === 'image/png' ? undefined : quality / 100,
    )
  })
}

async function compressEntry(
  entry: CompressEntry,
  request: CompressionRequest,
  copy: CompressCopy,
): Promise<CompressionResult> {
  if (!entry.originalUrl) {
    throw new CompressImageError(copy.errorsText.corrupt, 'corrupt_image')
  }

  const mime = sourceMime(entry.file)
  assertActive(request.signal)
  if (mime === 'image/webp' && !(await supportsWebpEncode())) {
    throw new CompressImageError(copy.errorsText.unsupportedWebp, 'unsupported_webp')
  }
  assertActive(request.signal)

  try {
    const image = await loadImage(entry.originalUrl, copy, request.signal)
    assertActive(request.signal)
    const sourceWidth = image.naturalWidth
    const sourceHeight = image.naturalHeight
    if (
      sourceWidth < 1 ||
      sourceHeight < 1 ||
      sourceWidth > MAX_DIMENSION ||
      sourceHeight > MAX_DIMENSION ||
      sourceWidth * sourceHeight > MAX_PIXELS
    ) {
      throw new CompressImageError(copy.errorsText.tooLargeImage, 'too_large_image')
    }

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) {
      throw new CompressImageError(copy.errorsText.canvas, 'canvas_error')
    }

    const encode = async ({
      quality,
      width,
      height,
      signal,
    }: {
      quality: number
      width: number
      height: number
      signal: AbortSignal
    }): Promise<Blob> => {
      assertActive(signal)
      canvas.width = width
      canvas.height = height
      assertActive(signal)
      if (mime === 'image/jpeg') {
        context.fillStyle = '#FFFFFF'
        context.fillRect(0, 0, width, height)
      }
      context.imageSmoothingEnabled = true
      context.imageSmoothingQuality = 'high'
      context.drawImage(image, 0, 0, width, height)
      assertActive(signal)
      return encodeCanvas(canvas, mime, quality, copy, signal)
    }

    try {
      if (request.mode === 'target_size' && mime !== 'image/png') {
        if (request.targetBytes === null) {
          throw new CompressImageError(copy.errorsText.invalidTarget, 'compression_failed')
        }
        const result = await searchTargetSize({
          sourceWidth,
          sourceHeight,
          targetBytes: request.targetBytes,
          signal: request.signal,
          encode,
        })
        return {
          blob: result.blob,
          width: result.width,
          height: result.height,
          actualQuality: result.quality,
          targetReached: result.reachedTarget,
          stopReason: result.stopReason,
        }
      }

      const blob = await encode({
        quality: request.quality,
        width: sourceWidth,
        height: sourceHeight,
        signal: request.signal,
      })
      const note =
        mime === 'image/png'
          ? request.mode === 'target_size'
            ? copy.pngTargetNotice
            : blob.size >= entry.originalSize
              ? copy.pngNoSavings
              : undefined
          : undefined

      return {
        blob,
        width: sourceWidth,
        height: sourceHeight,
        actualQuality: mime === 'image/png' ? null : request.quality,
        targetReached: null,
        note,
      }
    } finally {
      canvas.width = 0
      canvas.height = 0
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error
    }
    if (error instanceof CompressImageError) {
      throw error
    }
    throw new CompressImageError(copy.errorsText.compression, 'compression_failed')
  }
}

function revokeEntry(entry: CompressEntry): void {
  if (entry.originalUrl) {
    URL.revokeObjectURL(entry.originalUrl)
  }
  if (entry.compressedUrl) {
    URL.revokeObjectURL(entry.compressedUrl)
  }
}

function sizeDelta(original: number, compressed: number | null): number | null {
  if (original <= 0 || compressed === null) return null
  return Math.round(((compressed - original) / original) * 100)
}

export function CompressImagePage() {
  const t = useT()
  const copy = t.compressImage
  const [files, setFiles] = useState<CompressEntry[]>([])
  const [mode, setMode] = useState<CompressMode>('quality')
  const [quality, setQuality] = useState(78)
  const [targetPreset, setTargetPreset] = useState<TargetSizePreset>('100')
  const [targetValue, setTargetValue] = useState('100')
  const [targetUnit, setTargetUnit] = useState<TargetSizeUnit>('KB')
  const [isCompressing, setIsCompressing] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const filesRef = useRef<CompressEntry[]>([])
  const generationRef = useRef(0)
  const activeRunRef = useRef<ActiveCompressionRun | null>(null)
  const targetBytes = useMemo(
    () => parseTargetBytes(targetValue, targetUnit),
    [targetUnit, targetValue],
  )

  useEffect(() => {
    filesRef.current = files
  }, [files])

  const cancelActiveRun = useCallback(() => {
    const run = activeRunRef.current
    if (!run) return
    cancelRun(run)
    if (activeRunRef.current === run) {
      activeRunRef.current = null
    }
  }, [])

  useEffect(
    () => () => {
      generationRef.current += 1
      cancelActiveRun()
      filesRef.current.forEach(revokeEntry)
    },
    [cancelActiveRun],
  )

  const invalidateResults = useCallback(() => {
    generationRef.current += 1
    cancelActiveRun()
    setIsCompressing(false)
    setFiles((current) =>
      current.map((entry) => {
        if (entry.status !== 'done' && entry.status !== 'unreachable') {
          return entry
        }
        if (entry.compressedUrl) {
          URL.revokeObjectURL(entry.compressedUrl)
        }
        return {
          ...entry,
          compressedBlob: null,
          compressedUrl: null,
          compressedSize: null,
          resultWidth: null,
          resultHeight: null,
          actualQuality: null,
          targetReached: null,
          status: 'pending',
          error: undefined,
          note: undefined,
        }
      }),
    )
  }, [cancelActiveRun])

  const handleModeChange = useCallback(
    (nextMode: CompressMode) => {
      if (nextMode === mode || isCompressing) return
      setMode(nextMode)
      invalidateResults()
    },
    [invalidateResults, isCompressing, mode],
  )

  const handleQualityChange = useCallback(
    (nextQuality: number) => {
      if (nextQuality === quality || isCompressing) return
      setQuality(nextQuality)
      invalidateResults()
    },
    [invalidateResults, isCompressing, quality],
  )

  const applyTargetPreset = useCallback(
    (preset: Exclude<TargetSizePreset, 'custom'>) => {
      if (isCompressing) return
      const unchanged =
        targetPreset === preset && targetValue === preset && targetUnit === 'KB'
      if (unchanged) return
      setTargetPreset(preset)
      setTargetValue(preset)
      setTargetUnit('KB')
      invalidateResults()
    },
    [
      invalidateResults,
      isCompressing,
      targetPreset,
      targetUnit,
      targetValue,
    ],
  )

  const handleTargetValueChange = useCallback(
    (value: string) => {
      if (value === targetValue || isCompressing) return
      setTargetValue(value)
      setTargetPreset('custom')
      invalidateResults()
    },
    [invalidateResults, isCompressing, targetValue],
  )

  const handleTargetUnitChange = useCallback(
    (unit: TargetSizeUnit) => {
      if (unit === targetUnit || isCompressing) return
      setTargetUnit(unit)
      setTargetPreset('custom')
      invalidateResults()
    },
    [invalidateResults, isCompressing, targetUnit],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      if (isCompressing) return
      generationRef.current += 1
      cancelActiveRun()
      setIsCompressing(false)

      const entries = Array.from(incoming).map<CompressEntry>((file) => {
        const validationError = validateFile(file, copy)
        if (validationError) {
          trackToolRejection(
            analyticsContext(mode),
            analyticsErrorCode(validationError.code),
          )
          return {
            id: uid(),
            file,
            originalUrl: null,
            originalSize: file.size,
            compressedBlob: null,
            compressedUrl: null,
            compressedSize: null,
            resultWidth: null,
            resultHeight: null,
            actualQuality: null,
            targetReached: null,
            status: 'error',
            error: validationError.message,
          }
        }

        return {
          id: uid(),
          file,
          originalUrl: URL.createObjectURL(file),
          originalSize: file.size,
          compressedBlob: null,
          compressedUrl: null,
          compressedSize: null,
          resultWidth: null,
          resultHeight: null,
          actualQuality: null,
          targetReached: null,
          status: 'pending',
        }
      })

      setFiles((prev) => [...prev, ...entries])
    },
    [cancelActiveRun, copy, isCompressing, mode],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)

      if (isCompressing) return

      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles, isCompressing],
  )

  const removeFile = useCallback((id: string) => {
    if (isCompressing) return
    generationRef.current += 1
    cancelActiveRun()
    setIsCompressing(false)

    setFiles((prev) => {
      const entry = prev.find((file) => file.id === id)
      if (entry) {
        revokeEntry(entry)
      }
      return prev.filter((file) => file.id !== id)
    })
  }, [cancelActiveRun, isCompressing])

  const clearAll = useCallback(() => {
    if (isCompressing) return

    generationRef.current += 1
    cancelActiveRun()
    setIsCompressing(false)
    files.forEach(revokeEntry)
    setFiles([])
    nextId = 0
  }, [cancelActiveRun, files, isCompressing])

  const handleCompress = useCallback(async () => {
    const pending = files.filter((file) => file.status === 'pending')
    if (
      pending.length === 0 ||
      (mode === 'target_size' && targetBytes === null)
    ) {
      return
    }

    cancelActiveRun()
    const controller = new AbortController()
    const run: ActiveCompressionRun = {
      controller,
      operation: startToolOperation(analyticsContext(mode)),
      settled: false,
    }
    activeRunRef.current = run
    const generation = ++generationRef.current
    const isCurrent = () =>
      activeRunRef.current === run &&
      !controller.signal.aborted &&
      generation === generationRef.current
    setIsCompressing(true)

    try {
      const startTime = performance.now()
      const updated = [...files]
      let runSuccessCount = 0
      let pngOnlyCount = 0
      let firstFailureCode: CompressErrorCode | null = null

      for (const entry of pending) {
        if (!isCurrent()) return
        const index = updated.findIndex((file) => file.id === entry.id)
        if (index === -1) continue

        updated[index] = { ...entry, status: 'compressing', error: undefined, note: undefined }
        if (isCurrent()) {
          setFiles([...updated])
        }

        try {
          const result = await compressEntry(
            entry,
            {
              mode,
              quality,
              targetBytes,
              signal: controller.signal,
            },
            copy,
          )
          if (!isCurrent()) return
          const compressedUrl = URL.createObjectURL(result.blob)
          if (!isCurrent()) {
            URL.revokeObjectURL(compressedUrl)
            return
          }
          const unreachable = result.targetReached === false
          const note = unreachable
            ? result.stopReason === 'minimum_quality_and_dimensions'
              ? copy.targetUnreachable
              : result.stopReason === 'time_limit'
                ? copy.targetTimeLimit
                : copy.targetAttemptLimit
            : result.note
          updated[index] = {
            ...entry,
            compressedBlob: result.blob,
            compressedUrl,
            compressedSize: result.blob.size,
            resultWidth: result.width,
            resultHeight: result.height,
            actualQuality: result.actualQuality,
            targetReached: result.targetReached,
            status: unreachable ? 'unreachable' : 'done',
            note,
          }
          if (mode === 'target_size' && sourceMime(entry.file) === 'image/png') {
            pngOnlyCount += 1
          } else if (!unreachable) {
            runSuccessCount += 1
          } else if (!firstFailureCode) {
            firstFailureCode =
              result.stopReason === 'time_limit'
                ? 'time_limit'
                : result.stopReason === 'attempt_limit'
                  ? 'search_limit'
                  : 'target_unreachable'
          }
          setFiles([...updated])
        } catch (error) {
          if (
            (error instanceof Error && error.name === 'AbortError') ||
            !isCurrent()
          ) {
            return
          }
          const code =
            error instanceof CompressImageError ? error.code : 'compression_failed'
          firstFailureCode ??= code
          updated[index] = {
            ...entry,
            status: 'error',
            error: error instanceof Error ? error.message : copy.errorsText.generic,
          }
          setFiles([...updated])
        }
      }

      if (!isCurrent()) return
      const durationMs = Math.round(performance.now() - startTime)
      if (runSuccessCount > 0) {
        settleRunSuccess(run, durationMs)
      } else if (firstFailureCode) {
        settleRunError(run, analyticsErrorCode(firstFailureCode))
      } else if (pngOnlyCount > 0) {
        settleRunError(run, 'unsupported_input')
      } else {
        settleRunError(run, 'processing_failed')
      }
    } finally {
      if (!run.settled) {
        settleRunError(run, 'cancelled')
      }
      if (activeRunRef.current === run) {
        activeRunRef.current = null
        setIsCompressing(false)
      }
    }
  }, [cancelActiveRun, copy, files, mode, quality, targetBytes])

  const handleDownloadOne = useCallback((entry: CompressEntry) => {
    if (!entry.compressedBlob) return
    downloadBlob(entry.compressedBlob, outputFilename(entry.file.name, sourceMime(entry.file)))
  }, [])

  const handleDownloadAll = useCallback(async () => {
    const done = files.filter((file) => file.status === 'done' && file.compressedBlob)
    if (done.length === 0) return

    const { default: JSZip } = await import('jszip')
    const zip = new JSZip()
    const usedNames = new Set<string>()

    for (const entry of done) {
      zip.file(
        uniqueOutputFilename(entry.file.name, sourceMime(entry.file), usedNames),
        entry.compressedBlob!,
      )
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    downloadBlob(zipBlob, 'compressed-images.zip')
  }, [files])

  const stats = useMemo(() => {
    const pending = files.filter((file) => file.status === 'pending').length
    const done = files.filter((file) => file.status === 'done').length
    const unreachable = files.filter((file) => file.status === 'unreachable').length
    const errors =
      files.filter((file) => file.status === 'error').length + unreachable
    const totalOriginal = files.reduce((sum, file) => sum + file.originalSize, 0)
    const totalCompressed = files
      .filter((file) => file.status === 'done' && file.compressedSize !== null)
      .reduce((sum, file) => sum + (file.compressedSize ?? 0), 0)
    const hasPng = files.some((file) => isSupportedImage(file.file) && sourceMime(file.file) === 'image/png')
    const hasLossy = files.some((file) => {
      if (!isSupportedImage(file.file)) return false
      const mime = sourceMime(file.file)
      return mime === 'image/jpeg' || mime === 'image/webp'
    })

    return {
      pending,
      done,
      unreachable,
      errors,
      totalOriginal,
      totalCompressed,
      hasPng,
      hasLossy,
    }
  }, [files])

  const canCompress =
    files.length > 0 &&
    !isCompressing &&
    stats.pending > 0 &&
    (mode === 'quality' || targetBytes !== null)
  const totalDelta = sizeDelta(stats.totalOriginal, stats.done > 0 ? stats.totalCompressed : null)

  useEffect(() => {
    if (!canCompress) return

    const timer = window.setTimeout(() => {
      void handleCompress()
    }, 360)

    return () => window.clearTimeout(timer)
  }, [canCompress, handleCompress])

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={[copy.formats, t.common.maxFileSizePerFile]}
        collapsed={files.length > 0}
      />

      <div
        className={cn(
          'grid grid-cols-1 gap-6',
          files.length > 0 && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start',
        )}
      >
        <div className="min-w-0 space-y-6">
          <Card
            className={cn(
              'mx-auto max-w-3xl transition-colors',
              files.length > 0 && 'max-w-none',
              dropActive && 'border-primary bg-primary/5',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent className="p-4 text-center sm:p-8">
              <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-xs font-semibold text-muted-foreground">
                ZIP
              </div>
              <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
              <p
                className={cn(
                  'mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground',
                  files.length > 0 && 'hidden sm:block',
                )}
              >
                {copy.dropDescription}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                multiple
                disabled={isCompressing}
                className="hidden"
                onChange={handleInputChange}
              />
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  disabled={isCompressing}
                  onClick={() => inputRef.current?.click()}
                >
                  {copy.chooseFiles}
                </Button>
                {files.length > 0 && (
                  <Button variant="outline" disabled={isCompressing} onClick={clearAll}>
                    {t.converter.clearAll}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {files.length > 0 && (
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold tracking-normal">
                  {t.converter.filesTitle} ({files.length})
                </h2>
                <div className="flex gap-2">
                  {stats.done > 0 && (
                    <Button variant="outline" size="sm" onClick={handleDownloadAll}>
                      {t.converter.downloadAll} ({stats.done})
                    </Button>
                  )}
                  {canCompress && (
                    <Button size="sm" onClick={handleCompress}>
                      {copy.updatePreviewButton} ({stats.pending})
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {files.map((entry) => {
                  const compressedDelta = sizeDelta(entry.originalSize, entry.compressedSize)
                  const resultName = outputFilename(entry.file.name, sourceMime(entry.file))

                  return (
                    <Card key={entry.id} className="overflow-hidden shadow-sm">
                      <CardContent className="p-0">
                        <div className="grid grid-cols-2 gap-px bg-border">
                          <div className="bg-muted">
                            <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
                              <span>{copy.sourceLabel}</span>
                              <span>{displayFormat(entry)}</span>
                            </div>
                            <div className="aspect-video bg-background/65">
                              {entry.originalUrl ? (
                                <img
                                  src={entry.originalUrl}
                                  alt={entry.file.name}
                                  className="h-full w-full object-contain"
                                />
                              ) : (
                                <div className="grid h-full place-items-center">
                                  <span className="rounded-md border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground">
                                    {displayFormat(entry)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="bg-muted">
                            <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
                              <span>{copy.resultLabel}</span>
                              <span>{displayFormat(entry)}</span>
                            </div>
                            <div className="aspect-video bg-background/65">
                              {entry.compressedUrl ? (
                                <img
                                  src={entry.compressedUrl}
                                  alt={resultName}
                                  className="h-full w-full object-contain"
                                />
                              ) : (
                                <div className="grid h-full place-items-center">
                                  <span className="rounded-md border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground">
                                    {copy.waitingPreview}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4 p-4">
                          <div className="min-w-0">
                            <div
                              className="truncate text-sm font-medium"
                              title={entry.file.name}
                            >
                              {entry.file.name}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                                {displayFormat(entry)}
                              </span>
                              <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                {formatSize(entry.originalSize)}
                              </span>
                              {entry.compressedSize !== null && (
                                <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                  {formatSize(entry.compressedSize)}
                                  {compressedDelta !== null && (
                                    <span
                                      className={cn(
                                        'ml-1 font-medium',
                                        compressedDelta <= 0
                                          ? 'text-primary'
                                          : 'text-muted-foreground',
                                      )}
                                    >
                                      {compressedDelta > 0 ? '+' : ''}
                                      {compressedDelta}%
                                    </span>
                                  )}
                                </span>
                              )}
                              {entry.resultWidth !== null && entry.resultHeight !== null && (
                                <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                  {entry.resultWidth} x {entry.resultHeight}
                                </span>
                              )}
                              {entry.actualQuality !== null && (
                                <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                  {copy.actualQuality}: {entry.actualQuality}%
                                </span>
                              )}
                            </div>
                          </div>

                          {entry.status === 'compressing' && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              {copy.compressing}
                            </div>
                          )}

                          {entry.note && (
                            <div className="rounded-md border bg-muted px-3 py-2 text-xs leading-5 text-muted-foreground">
                              {entry.note}
                            </div>
                          )}

                          {entry.status === 'done' && entry.compressedBlob && (
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="text-sm font-medium text-primary">
                                {entry.targetReached
                                  ? copy.targetReached
                                  : t.common.done}
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadOne(entry)}
                              >
                                {t.common.download}
                              </Button>
                            </div>
                          )}

                          {entry.status === 'unreachable' && entry.compressedBlob && (
                            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2">
                              <span className="text-sm text-destructive">
                                {copy.bestFound}: {formatSize(entry.compressedBlob.size)}
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadOne(entry)}
                              >
                                {copy.downloadBest}
                              </Button>
                            </div>
                          )}

                          {entry.status === 'error' && (
                            <div className="text-sm text-destructive">
                              {entry.error ?? copy.errorsText.generic}
                            </div>
                          )}

                          {entry.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isCompressing}
                              className="text-destructive hover:text-destructive"
                              onClick={() => removeFile(entry.id)}
                            >
                              {t.common.remove}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </section>
          )}
        </div>

        {files.length > 0 && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">
                    {t.converter.settings}
                  </h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">{copy.modeLabel}</div>
                  <div className="grid grid-cols-2 overflow-hidden rounded-md border">
                    <button
                      type="button"
                      disabled={isCompressing}
                      className={cn(
                        'px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50',
                        mode === 'quality'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background hover:bg-muted',
                      )}
                      onClick={() => handleModeChange('quality')}
                    >
                      {copy.qualityMode}
                    </button>
                    <button
                      type="button"
                      disabled={isCompressing}
                      className={cn(
                        'border-l px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50',
                        mode === 'target_size'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background hover:bg-muted',
                      )}
                      onClick={() => handleModeChange('target_size')}
                    >
                      {copy.targetMode}
                    </button>
                  </div>
                </div>

                {mode === 'quality' ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">{copy.qualityLabel}</span>
                      <span className="text-sm tabular-nums text-muted-foreground">
                        {quality}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min={30}
                      max={95}
                      step={1}
                      value={quality}
                      disabled={isCompressing || !stats.hasLossy}
                      onChange={(event) =>
                        handleQualityChange(Number(event.target.value))
                      }
                      className="w-full accent-primary disabled:opacity-50"
                    />
                    <p className="text-xs leading-5 text-muted-foreground">
                      {stats.hasLossy ? copy.qualityHint : copy.pngOnlyHint}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="text-sm font-medium">{copy.targetPresetLabel}</div>
                      <div className="grid grid-cols-5 overflow-hidden rounded-md border">
                        {(['50', '100', '200', '500'] as const).map((preset) => (
                          <button
                            key={preset}
                            type="button"
                            disabled={isCompressing}
                            className={cn(
                              'border-l px-2 py-2 text-xs font-medium first:border-l-0 disabled:opacity-50',
                              targetPreset === preset
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-background hover:bg-muted',
                            )}
                            onClick={() => applyTargetPreset(preset)}
                          >
                            {preset} KB
                          </button>
                        ))}
                        <button
                          type="button"
                          disabled={isCompressing}
                          className={cn(
                            'border-l px-2 py-2 text-xs font-medium disabled:opacity-50',
                            targetPreset === 'custom'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-background hover:bg-muted',
                          )}
                          onClick={() => setTargetPreset('custom')}
                        >
                          {copy.customTarget}
                        </button>
                      </div>
                    </div>

                    <label className="block space-y-2">
                      <span className="text-sm font-medium">{copy.targetValueLabel}</span>
                      <div className="grid grid-cols-[minmax(0,1fr)_5rem]">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={targetValue}
                          disabled={isCompressing}
                          aria-invalid={targetBytes === null}
                          onChange={(event) => handleTargetValueChange(event.target.value)}
                          className="rounded-l-md border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                        />
                        <select
                          value={targetUnit}
                          disabled={isCompressing}
                          onChange={(event) =>
                            handleTargetUnitChange(event.target.value as TargetSizeUnit)
                          }
                          className="rounded-r-md border border-l-0 bg-background px-2 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                        >
                          <option value="KB">KB</option>
                          <option value="MB">MB</option>
                        </select>
                      </div>
                    </label>

                    <p
                      className={cn(
                        'text-xs leading-5',
                        targetBytes === null
                          ? 'text-destructive'
                          : 'text-muted-foreground',
                      )}
                    >
                      {targetBytes === null
                        ? copy.invalidTarget
                        : copy.targetHint}
                    </p>
                  </div>
                )}

                {stats.hasPng && (
                  <p className="rounded-md border bg-background px-3 py-2 text-xs leading-5 text-muted-foreground">
                    {mode === 'target_size' ? copy.pngTargetNotice : copy.pngNotice}
                  </p>
                )}

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleCompress}
                  disabled={!canCompress}
                >
                  {isCompressing
                    ? copy.compressing
                    : stats.pending > 0
                      ? `${copy.updatePreviewButton} (${stats.pending})`
                      : t.converter.noFilesQueued}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{t.converter.summary}</h2>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.pending}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.pending}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.done}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.done}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.errors}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.errors}</div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{copy.originalSize}</span>
                    <strong className="font-medium text-foreground">
                      {formatSize(stats.totalOriginal)}
                    </strong>
                  </div>
                  {stats.done > 0 && (
                    <div className="flex justify-between gap-3">
                      <span>{copy.compressedSize}</span>
                      <strong className="font-medium text-foreground">
                        {formatSize(stats.totalCompressed)}
                        {totalDelta !== null && (
                          <span
                            className={cn(
                              'ml-1',
                              totalDelta <= 0 ? 'text-primary' : 'text-muted-foreground',
                            )}
                          >
                            {totalDelta > 0 ? '+' : ''}
                            {totalDelta}%
                          </span>
                        )}
                      </strong>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {copy.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section
        data-compress-howto
        className="mt-12 border-t pt-8"
      >
        <h2 className="text-2xl font-semibold tracking-normal">{copy.howToTitle}</h2>
        <ol className="mt-4 grid gap-3 md:grid-cols-3">
          {copy.howTo.map((step, index) => (
            <li
              key={step}
              className="rounded-md border bg-card p-4 text-sm leading-6 text-muted-foreground"
            >
              <span className="mb-2 block text-xs font-semibold text-primary">
                {index + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}
